import { useEffect, useMemo, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import { FormattedMessage } from 'react-intl';
import { Field } from '../../../../components/field';
import { usePDP } from '../../../../contexts/pdp/usePDP';
import { Text } from '../../../../ui-components';
import { ChipList } from '../../../../ui-components/variant/chip/ChipList';
import { useGiftCardLocation } from '../hooks/useGiftCardLocation';
import type { FieldConfig } from '../../../../components/field';
import type { ChipItem } from '../../../../ui-components/variant/chip/ChipList';

export interface GiftCardBuyBoxForm {
    giftCardAmount: string;
    onResetFormFields: (fields: Record<string, string>) => void;
    giftCardAmountField: FieldConfig;
}

const amountList: ChipItem[] = [
    {
        id: '1',
        label: '$50',
        value: '50',
    },
    {
        id: '2',
        label: '$100',
        value: '100',
    },
    {
        id: '3',
        label: '$150',
        value: '150',
    },
    {
        id: '4',
        label: '$250',
        value: '250',
    },
    {
        id: '5',
        label: '$500',
        value: '500',
    },
];
export const GiftCardAmount: React.FC<GiftCardBuyBoxForm> = ({
    giftCardAmount,
    onResetFormFields,
    giftCardAmountField,
}: GiftCardBuyBoxForm) => {
    const { updateLocationAmount } = useGiftCardLocation();
    const [activeId, setActiveId] = useState<string | null>(null);
    const { giftCardAmount: selectedAmount, setGiftCardAmount } = usePDP();

    const chooseAmount = giftCardAmount || selectedAmount;

    useEffect(() => {
        if (giftCardAmount) {
            setActiveId('0');
        } else if (selectedAmount) {
            const foundAmount = amountList.find(
                (amountItem) => amountItem.value === selectedAmount,
            );

            if (foundAmount) {
                setActiveId(foundAmount.id);
            }
        }
    }, [giftCardAmount, selectedAmount]);

    const onChangeAmount = (id: string): void => {
        onResetFormFields({ giftCardAmount: '' });
        setActiveId(id);

        const newAmount = amountList.find((amountItem) => amountItem.id === id)?.value || '0';

        setGiftCardAmount(newAmount);
        updateLocationAmount(newAmount);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement> | undefined): void => {
        if (event?.target?.value === '') {
            setGiftCardAmount('50');
        }
    };

    const activeAmountId: string | undefined = useMemo<string | undefined>(
        () => amountList.find((amountItem) => amountItem.id === activeId)?.id,
        [activeId],
    );
    const formatSelectedAmount = (amount: string): string => `$${Number(amount).toFixed(2)}`;

    const { min, max } = getMinAndMax(giftCardAmountField?.rules);
    const isInBetween = Number(chooseAmount) >= min && Number(chooseAmount) <= max;

    return (
        <Flex direction="column" gap={2} data-testid="gift-card-amount-container">
            <Flex gap={1} data-testid="gift-card-amount-header">
                <Text style="bold">
                    <FormattedMessage
                        id="gift_card.details_page.choose_amount"
                        defaultMessage="Choose an Amount"
                    />
                </Text>
                <Text>{formatSelectedAmount(isInBetween ? chooseAmount || '0' : '0')}</Text>
            </Flex>
            <Flex gap={4} direction={{ base: 'column', sm: 'row', md: 'column' }}>
                <ChipList
                    activeId={activeAmountId}
                    onClick={onChangeAmount}
                    chipListItems={amountList}
                />
                <Flex flex={1}>
                    <Field
                        variant="new-style"
                        {...giftCardAmountField}
                        leftText={
                            <FormattedMessage
                                defaultMessage="$"
                                id="gift_card.details_page.dollar_sign"
                            />
                        }
                        inputProps={{
                            paddingLeft: '20px',
                            maxLength: 3,
                            onBlur: handleBlur,
                        }}
                        type="number"
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

function getMinAndMax(rules: unknown): { min: number; max: number } {
    if (has(rules, 'min.value') && has(rules, 'max.value')) {
        return {
            min: Number(get(rules, 'min.value')),
            max: Number(get(rules, 'max.value')),
        };
    }

    return {
        min: 0,
        max: 500,
    };
}
